/**
 * <s-summit-log>
 * Brand page chronicle on the altitude scale (snippets/page-brand.liquid).
 * When the log scrolls into view, the [data-summit-entry] items (DOM order = chronological,
 * shown bottom-up) appear one after another and --climb grows so the brass line follows.
 * Reduced motion or no IntersectionObserver: everything is shown at once.
 */
const STEP_MS = 420;

class SSummitLog extends HTMLElement {
  #observer = null;
  #timers = [];

  connectedCallback() {
    const entries = [...this.querySelectorAll('[data-summit-entry]')];
    if (!entries.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      this.#showAll(entries);
      return;
    }
    entries.forEach((entry) => entry.classList.add('opacity-0', 'translate-y-3'));
    this.style.setProperty('--climb', '0');
    this.#observer = new IntersectionObserver((records) => {
      if (!records.some((record) => record.isIntersecting)) return;
      this.#observer?.disconnect();
      entries.forEach((entry, index) => {
        this.#timers.push(window.setTimeout(() => {
          entry.classList.remove('opacity-0', 'translate-y-3');
          entry.toggleAttribute('data-on', true);
          this.style.setProperty('--climb', String(this.#climbTo(entries, entry)));
        }, 250 + index * STEP_MS));
      });
    }, { threshold: 0.25 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#timers.forEach((timer) => window.clearTimeout(timer));
    this.#timers = [];
  }

  #showAll(entries) {
    entries.forEach((entry) => entry.toggleAttribute('data-on', true));
    this.style.setProperty('--climb', '1');
  }

  /** Share of the scale (from its base) up to this entry's diamond. */
  #climbTo(entries, entry) {
    const list = entry.parentElement;
    const listRect = list.getBoundingClientRect();
    const rect = entry.getBoundingClientRect();
    if (!listRect.height) return 1;
    const fromBottom = listRect.bottom - (rect.top + 6);
    return Math.min(1, Math.max(0, fromBottom / listRect.height));
  }
}

if (!customElements.get('s-summit-log')) customElements.define('s-summit-log', SSummitLog);
